import React, { useEffect, useState } from "react";
import { Checkbox, InputLabel, ListItemIcon, ListItemText, MenuItem, Select, Grid } from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 7.5 + ITEM_PADDING_TOP,
            width: "max-width",
        },
    },
    getcontentanchorel: null,
    anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
    },
    transformOrigin: {
        vertical: "top",
        horizontal: "left",
    },
    variant: "menu",
};

const renderError = (formik, keyField) => {
    let keyFieldParts = keyField?.split('.');

    if (keyFieldParts?.length === 3 && formik?.errors[keyFieldParts[0]]) {
        let errorString = formik?.errors[keyFieldParts[0]]?.[keyFieldParts[1]]?.[keyFieldParts[2]];
        return (
            <div>
                {errorString ? <div className="error-text">{errorString}</div> : null}
            </div>
        );
    }

    return (
        <div>
            {formik?.errors[keyField] ? (
                <div className="error-text">{formik?.errors[keyField]}</div>
            ) : null}
        </div>
    );
};

const coerceToArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === "") return [];
    return [value];
};

const valuesMatch = (a, b) => String(a) === String(b);

const normalizeSelection = (value, options) => {
    const raw = coerceToArray(value);
    const matched = [];

    raw.forEach((val) => {
        if (val === "Select" || val === "selectAll") return;
        const option = options.find((opt) => valuesMatch(opt.value, val));
        if (option && !matched.some((m) => valuesMatch(m, option.value))) {
            matched.push(option.value);
        }
    });

    return matched;
};

function MultiSelectV2({
    options,
    keyField,
    formik,
    isSearchable = "false",
    isSelectAll = false,
    isSelectAllEnable = false,
    label,
    nodata,
    required = true,
    hideRequired = false,
    defaultValue = [],
    readOnly = false,
    bindMethod = false,
    ...props
}) {
    const [selected, setSelected] = useState([]);
    const [selectAll, setSelectAll] = useState(isSelectAll);
    const isSmallScreen = useMediaQuery('(max-width: 991px)');
    const isBigScreen = useMediaQuery('(max-width: 1395px)');

    const applySelection = (nextSelected) => {
        const validSelected = normalizeSelection(nextSelected, options);

        setSelected(validSelected);
        setSelectAll(
            validSelected.length > 0 && validSelected.length === options.length
        );

        if (keyField) {
            formik?.setFieldValue(keyField, validSelected);
        }
        if (bindMethod) {
            bindMethod(validSelected);
        }
    };

    const handleChange = (event) => {
        if (readOnly || props.disabled) return;
        const value = coerceToArray(event.target.value);
        if (value.includes("selectAll")) {
            handleSelectAll();
            return;
        }
        applySelection(value);
    };

    useEffect(() => {
        const validIds = normalizeSelection(defaultValue, options);
        setSelected(validIds);
        setSelectAll(validIds.length > 0 && validIds.length === options.length);
    }, [JSON.stringify(defaultValue), JSON.stringify(options.map((opt) => opt.value))]);

    useEffect(() => {
        if (!isSelectAllEnable || !keyField) return;
        const fieldLength = formik?.values?.[keyField]?.length ?? 0;
        if (fieldLength === 0) {
            setSelected([]);
            setSelectAll(false);
        } else if (options?.length > 0 && fieldLength === options.length) {
            setSelectAll(true);
        }
    }, [formik?.values?.[keyField]?.length, isSelectAllEnable, keyField, options?.length]);

    useEffect(() => {
        setSelectAll(isSelectAll);
    }, [isSelectAll]);

    const handleSelected = (selectedValues) => {
        if (!selectedValues?.length) {
            return "Select";
        }

        return selectedValues
            .map((val) => {
                const matchedOption = options.find((option) =>
                    valuesMatch(option.value, val)
                );
                return matchedOption ? matchedOption.name : val;
            })
            .join(", ");
    };

    const handleSelectAll = () => {
        const allSelected =
            selected.length === options.length && options.length > 0;
        applySelection(
            allSelected ? [] : options.map((option) => option.value)
        );
    };

    const isOptionSelected = (optionValue) =>
        selected.some((val) => valuesMatch(val, optionValue));

    const toggleOption = (optionValue) => {
        const next = isOptionSelected(optionValue)
            ? selected.filter((val) => !valuesMatch(val, optionValue))
            : [...selected, optionValue];
        applySelection(next);
    };

    const renderMenuItem = ({ value, name }, useManualToggle = false) => {
        const menuItemStyle = {
            textTransform: "capitalize",
            whiteSpace: "normal",
            wordWrap: "break-word",
            height: "auto",
            minHeight: "48px",
        };

        if (useManualToggle) {
            return (
                <MenuItem
                    key={value}
                    onClick={() => toggleOption(value)}
                    style={menuItemStyle}
                >
                    <ListItemIcon>
                        <Checkbox
                            checked={isOptionSelected(value)}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleOption(value);
                            }}
                        />
                    </ListItemIcon>
                    <ListItemText
                        primary={name}
                        primaryTypographyProps={{
                            style: {
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                            },
                        }}
                    />
                </MenuItem>
            );
        }

        return (
            <MenuItem key={value} value={value} style={menuItemStyle}>
                <ListItemIcon>
                    <Checkbox checked={isOptionSelected(value)} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText
                    primary={name}
                    primaryTypographyProps={{
                        style: {
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                        },
                    }}
                />
            </MenuItem>
        );
    };

    const shouldUseMultiColumn = options.length >= 15;
    let firstColumnOptions = options;
    let secondColumnOptions = [];

    if (shouldUseMultiColumn) {
        const halfLength = Math.ceil(options.length / 2);
        firstColumnOptions = options.slice(0, halfLength);
        secondColumnOptions = options.slice(halfLength);
    }

    const renderOption = (option) => renderMenuItem(option, shouldUseMultiColumn);

    return (
        <div className="field-wrap multiselect">
            {label && (
                <InputLabel id="mutiple-select-label">
                    {label}
                    {hideRequired ? null : required ? (
                        <span style={{ color: "red" }}> *</span>
                    ) : props.propsOptionData === 0 ? null : (
                        " (Optional)"
                    )}
                </InputLabel>
            )}
            <Select
                labelId="mutiple-select-label"
                multiple
                displayEmpty
                value={selected}
                onChange={handleChange}
                renderValue={(selectedValues) => handleSelected(selectedValues)}
                MenuProps={{
                    ...MenuProps,
                    PaperProps: {
                        ...MenuProps.PaperProps,
                        style: {
                            ...MenuProps.PaperProps.style,
                            maxHeight: ITEM_HEIGHT * 7.5 + ITEM_PADDING_TOP,
                        },
                    },
                }}
                fullWidth
                issearchable={isSearchable}
                style={{
                    borderRadius: "10px",
                    height: "42px",
                    minHeight: isBigScreen ? "54px" : "50px",
                    maxWidth: isSmallScreen ? "100%" : "clamp(23rem, 3.5vw, 30rem)",
                    textTransform: "capitalize",
                }}
                readOnly={readOnly}
                {...props}
            >
                {isSelectAllEnable && (
                    <MenuItem
                        value="selectAll"
                        style={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "white",
                            zIndex: 1,
                        }}
                    >
                        <ListItemIcon>
                            <Checkbox checked={selectAll} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary="Select All" />
                    </MenuItem>
                )}
                {options.length > 0 ? (
                    shouldUseMultiColumn ? (
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                {firstColumnOptions.map(renderOption)}
                            </Grid>
                            <Grid item xs={6}>
                                {secondColumnOptions.map(renderOption)}
                            </Grid>
                        </Grid>
                    ) : (
                        options.map(renderOption)
                    )
                ) : (
                    <MenuItem disabled>
                        <ListItemText primary={nodata} />
                    </MenuItem>
                )}
            </Select>
            {renderError(formik, keyField)}
        </div>
    );
}

export default MultiSelectV2;
